import React from 'react';
import { Box, Button, Chip, IconButton, Menu, MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { RecordDialog } from '../components/RecordDialog';
import { UserPremiumDialog } from '../components/UserPremiumDialog';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';
import { formatDateTime } from '../utils/date';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const statusColor = (s) => {
  if (s === 'active') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'blocked') return 'error';
  return 'default';
};

export function AgentsPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);

  const [page, setPage] = React.useState(0); // DataGrid is 0-based
  const [pageSize, setPageSize] = React.useState(20);
  const [status, setStatus] = React.useState('');
  const [search, setSearch] = React.useState('');

  const canWrite = admin?.role === 'SUPER_ADMIN' || admin?.role === 'ADMIN';
  const isSuper = admin?.role === 'SUPER_ADMIN';
  const [viewOpen, setViewOpen] = React.useState(false);
  const [premiumOpen, setPremiumOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [savingPremium, setSavingPremium] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/agents', {
        params: {
          take: pageSize,
          page: page + 1,
          status: status || undefined,
          search: search || undefined,
        },
      });
      const data = unwrapApiResponse(res);
      setRows(data?.agents || []);
      setRowCount(data?.total || 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, search]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (id, nextStatus) => {
    await http.put(`/admin/agents/${id}/status`, { status: nextStatus });
    await fetchData();
  };

  const deleteAgent = async (id) => {
    await http.delete(`/admin/agents/${id}`);
    await fetchData();
  };

  const viewAgent = (row) => {
    setSelected(row);
    setViewOpen(true);
  };

  const openPremium = (row) => {
    // Premium update endpoint works on `users` table; agents are also users.
    setSelected(row);
    setPremiumOpen(true);
  };

  function AgentActionsCell({ row }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const close = () => setAnchorEl(null);

    const onAction = async (fn) => {
      close();
      await fn();
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          aria-label={t('agents.colActions')}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={(e) => {
            e?.stopPropagation?.();
            close();
          }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.25,
                minWidth: 200,
              },
            },
          }}
        >
          <MenuItem onClick={() => onAction(() => viewAgent(row))}>{t('common.view')}</MenuItem>
          <MenuItem disabled={!canWrite} onClick={() => onAction(() => openPremium(row))}>
            {t('common.premium')}
          </MenuItem>
          <MenuItem onClick={() => onAction(() => updateStatus(row.id, 'active'))}>{t('agents.approve')}</MenuItem>
          <MenuItem onClick={() => onAction(() => updateStatus(row.id, 'pending'))}>{t('agents.pending')}</MenuItem>
          <MenuItem onClick={() => onAction(() => updateStatus(row.id, 'blocked'))}>{t('agents.block')}</MenuItem>
          {isSuper ? (
            <MenuItem
              onClick={() =>
                onAction(async () => {
                  const ok = window.confirm(t('agents.deleteConfirm', { id: row.id }));
                  if (!ok) return;
                  try {
                    setError(null);
                    await deleteAgent(row.id);
                  } catch (e) {
                    setError(e);
                  }
                })
              }
              sx={{ color: 'error.main' }}
            >
              {t('agents.delete')}
            </MenuItem>
          ) : null}
        </Menu>
      </>
    );
  }

  const savePremium = async (patch) => {
    if (!selected?.id) return;
    setSavingPremium(true);
    setError(null);
    try {
      await http.put(`/admin/users/${selected.id}/premium`, patch);
      setPremiumOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSavingPremium(false);
    }
  };

  const columns = [
    { field: 'id', headerName: t('agents.colId'), width: 80 },
    { field: 'agency_name', headerName: t('agents.colAgency'), flex: 1, minWidth: 200 },
    { field: 'email', headerName: t('agents.colEmail'), flex: 1, minWidth: 220 },
    { field: 'phone', headerName: t('agents.colPhone'), width: 160 },
    {
      field: 'status',
      headerName: t('agents.colStatus'),
      width: 130,
      renderCell: (p) => <Chip size="small" label={p.value || '—'} color={statusColor(p.value)} />,
    },
    { field: 'created_at', headerName: t('agents.colCreated'), width: 150, valueFormatter: (v) => formatDateTime(v) },
    {
      field: 'actions',
      headerName: t('agents.colActions'),
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (p) => <AgentActionsCell row={p.row} />,
    },
  ];

  return (
    <Box>
      <PageHeader
        title={t('agents.title')}
        subtitle={t('agents.subtitle')}
        right={
          <Button variant="outlined" onClick={fetchData} disabled={loading}>
            {t('common.refresh')}
          </Button>
        }
      />

      <ErrorBanner error={error} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          label={t('agents.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', md: 340 } }}
        />
        <TextField
          select
          label={t('agents.status')}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', md: 220 } }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          <MenuItem value="pending">{t('agents.statusPending')}</MenuItem>
          <MenuItem value="active">{t('agents.statusActive')}</MenuItem>
          <MenuItem value="blocked">{t('agents.statusBlocked')}</MenuItem>
        </TextField>
      </Stack>

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          density="compact"
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <RecordDialog
        open={viewOpen}
        title={t('agents.dialogTitle', { id: selected?.id || '' })}
        record={selected}
        onClose={() => setViewOpen(false)}
      />

      <UserPremiumDialog
        open={premiumOpen}
        user={selected}
        saving={savingPremium}
        onClose={() => setPremiumOpen(false)}
        onSave={savePremium}
      />
    </Box>
  );
}

